import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/authProvider";
import { useBlocks } from "../contexts/blockProvider";
import { listenToUserChats } from "../services/chatService";
import Icon from "./AppIcon";
import { PresenceIndicator } from "./PresenceIndicator";
import { AvatarWithBadge } from "./SubscriptionBadge";
import logger from "utils/logger";

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [allChats, setAllChats] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isBlockedByMe, blockedByMeIds } = useBlocks();

  // Telas com barra de ação fixa no rodapé abaixo de `lg` (Filtros/Buscar na
  // busca; Curtir/Chat no perfil de outra pessoa). O botão flutuante ficava
  // na mesma faixa e cobria metade do "Buscar" — sobe para cima da barra.
  const temBarraDeAcao =
    location.pathname === "/dash/advanced-search" ||
    (location.pathname === "/dash/profile-details" &&
      new URLSearchParams(location.search).has("userUid"));

  // A lista vem do Firestore direto para o cliente: quem eu bloqueei precisa
  // sumir daqui também, senão o widget continua pingando não lidas de alguém
  // que já não aparece na tela de conversas.
  const visibleChats = useMemo(
    () => allChats.filter((c) => !isBlockedByMe(c?.otherUserId)),
    // `isBlockedByMe` é recriado a cada render do provider; `blockedByMeIds`
    // é o dado que realmente muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allChats, blockedByMeIds],
  );
  const chats = useMemo(() => visibleChats.slice(0, 3), [visibleChats]);
  const unreadCount = useMemo(
    () => visibleChats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0),
    [visibleChats],
  );

  useEffect(() => {
    if (!user) {
      logger.log("FloatingChat: Sem usuário autenticado");
      return;
    }

    logger.log(
      "FloatingChat: Iniciando listener de chats para usuário:",
      user.uid,
    );

    try {
      // Listener para os chats do usuário
      const unsubscribe = listenToUserChats(
        (userChats) => {
          logger.log("FloatingChat: ✅ Callback chamado!");
          logger.log("FloatingChat: Chats recebidos:", userChats?.length || 0);
          logger.log("FloatingChat: Array de chats:", userChats);

          if (!userChats || userChats.length === 0) {
            logger.log("FloatingChat: Nenhum chat encontrado ou array vazio");
            setAllChats([]);
            return;
          }

          // Ordenar por última mensagem. O corte em 3 e a soma de não lidas
          // saem dos memos acima, já sem as conversas bloqueadas.
          const sortedChats = [...userChats].sort((a, b) => {
            const aTime = a.lastMessageTimestamp?.toMillis?.() || 0;
            const bTime = b.lastMessageTimestamp?.toMillis?.() || 0;
            return bTime - aTime;
          });

          setAllChats(sortedChats);
        },
        (error) => {
          logger.error("FloatingChat: ❌ Erro no listener:", error);
        },
      );

      if (!unsubscribe) {
        logger.error(
          "FloatingChat: ❌ listenToUserChats não retornou função de unsubscribe",
        );
      } else {
        logger.log("FloatingChat: ✅ Listener configurado com sucesso");
      }

      return () => {
        logger.log("FloatingChat: Limpando listener");
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      logger.error("FloatingChat: Erro ao criar listener:", error);
    }
  }, [user]);

  const handleChatClick = (chat) => {
    navigate(`/dash/chat-conversation?chatId=${chat.chatId || chat.id}`);
    setIsOpen(false);
  };

  const handleOpenAllChats = () => {
    navigate("/dash/chat-conversation");
    setIsOpen(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Agora";
    if (diffInMinutes < 60) return `${diffInMinutes}min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "Ontem";
    if (diffInDays < 7) return `${diffInDays}d`;

    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  // Teste: sempre renderizar para debug
  logger.log(
    "FloatingChat: Renderizando componente - isOpen:",
    isOpen,
    "user:",
    !!user,
    "chats:",
    chats.length,
  );

  return (
    // z-[45]: acima das barras de ação fixas (z-40) e abaixo dos modais e
    // folhas (z-50 em diante). Estava em 9999 — resto de depuração — e o botão
    // ficava por cima de todo modal: no celular cobria o "Salvar alterações"
    // do Editar Perfil e parte dos filtros da busca.
    <div className="relative z-[45]">
      {/* Chat Widget */}
      {isOpen && (
        <div
          className={`fixed right-4 md:right-6 w-[min(340px,calc(100vw-2rem))] md:w-[380px] bg-card border border-border rounded-xl shadow-elevation-3 animate-slide-up ${
            temBarraDeAcao
              ? "bottom-nav mb-[9.5rem] md:bottom-0 md:mb-[9.75rem] lg:bottom-24 lg:mb-0"
              : "bottom-nav md:bottom-24 mb-[4.5rem] md:mb-0"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-pink-500/10 to-purple-500/10">
            <div className="flex items-center space-x-2">
              <Icon name="MessageCircle" size={20} className="text-primary" />
              <h3 className="font-heading font-semibold text-foreground">
                Mensagens
              </h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted/50 transition-micro"
            >
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Chat List */}
          <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
            {chats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-3">
                  <Icon
                    name="MessageCircle"
                    size={24}
                    className="text-muted-foreground"
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Nenhuma conversa ativa
                </p>
              </div>
            ) : (
              chats.map((chat) => {
                // Proteção contra dados undefined
                if (!chat || !chat.id) return null;

                return (
                  <button
                    key={chat.id}
                    onClick={() => handleChatClick(chat)}
                    className="w-full flex items-start space-x-3 p-4 hover:bg-muted/50 transition-micro border-b border-border last:border-b-0"
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {chat.otherUserAvatar ? (
                        <>
                          <AvatarWithBadge
                            src={chat.otherUserAvatar}
                            alt={chat.otherUserName || "Usuário"}
                            size="medium"
                            planId={chat.planId}
                          />
                          {chat.otherUserId && (
                            <div className="absolute bottom-0 right-0">
                              <PresenceIndicator
                                userId={chat.otherUserId}
                                showLabel={false}
                                size="sm"
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                          <Icon
                            name="User"
                            size={24}
                            className="text-muted-foreground"
                          />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-sm text-foreground truncate">
                          {chat.otherUserName || "Usuário"}
                        </h4>
                        {chat.lastMessageTimestamp && (
                          <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                            {formatTime(chat.lastMessageTimestamp)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground truncate">
                          {chat.lastMessageText || "Iniciar conversa"}
                        </p>
                        {chat.unreadCount > 0 && (
                          <span className="ml-2 flex-shrink-0 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-[10px] text-primary-foreground font-bold">
                            {chat.unreadCount > 9 ? "9+" : chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-border bg-muted/30">
            <button
              onClick={handleOpenAllChats}
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-micro font-medium text-sm"
            >
              Ver todas as conversas
            </button>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => {
          logger.log("FloatingChat: Botão clicado, isOpen antes:", isOpen);
          setIsOpen(!isOpen);
        }}
        aria-label={isOpen ? "Fechar mensagens" : "Abrir mensagens"}
        className={`fixed right-4 md:right-6 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-full shadow-elevation-3 flex items-center justify-center transition-all duration-300 hover:scale-110 group ${
          temBarraDeAcao
            ? "bottom-nav mb-[5.25rem] md:bottom-0 md:mb-[5.5rem] lg:bottom-6 lg:mb-0"
            : "bottom-nav md:bottom-6 mb-4 md:mb-0"
        }`}
      >
        {isOpen ? (
          <Icon name="X" size={24} className="text-white" />
        ) : (
          <>
            <Icon name="MessageCircle" size={24} className="text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 border-2 border-white dark:border-gray-900 rounded-full flex items-center justify-center text-xs font-bold text-white animate-pulse">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

export default FloatingChat;
